/**
 * Edit "generation" tracking for per-colour/side print exports.
 *
 * WHY THIS EXISTS: a colour/side saves its thumbnail, ElementPlacement and print file from three
 * independent sources. A print export started BEFORE the designer shrank/moved the logo stayed
 * tracked in pendingExports and was saved next to the post-edit thumbnail and placement (a 2.5"
 * chest logo saved with a 12"-wide print). Every design change bumps the side's generation; an
 * export only counts if it was started at, and completes at, the current generation.
 *
 * No Angular dependencies — the component computes the keys, these rules decide.
 */
export class PrintExportGenerations {
  private current = new Map<string, number>();
  private started = new Map<string, number>();
  private accepted = new Map<string, number>();

  /** The side's design changed: older exports are stale from now on. Returns the new generation. */
  bump(key: string): number {
    const next = this.get(key) + 1;
    this.current.set(key, next);
    return next;
  }

  get(key: string): number {
    return this.current.get(key) || 0;
  }

  /** An export is being started now; returns the generation it belongs to. */
  begin(key: string): number {
    const gen = this.get(key);
    this.started.set(key, gen);
    return gen;
  }

  /** True when an export that began at `gen` still reflects the side's current design. */
  isCurrent(key: string, gen: number): boolean {
    return this.get(key) === gen;
  }

  /** Record that an export for generation `gen` produced a print file. */
  accept(key: string, gen: number): boolean {
    if (!this.isCurrent(key, gen)) {
      return false;
    }
    this.accepted.set(key, gen);
    return true;
  }

  /** Edited since the last accepted print file (never-edited sides keep today's behaviour). */
  isStale(key: string): boolean {
    const gen = this.get(key);
    return gen > 0 && this.accepted.get(key) !== gen;
  }

  /** Edited, and no export has been started for the current design yet. */
  needsNewExport(key: string): boolean {
    return this.isStale(key) && this.started.get(key) !== this.get(key);
  }
}

/**
 * Drop a side's tracked job id + print URL from a variant's export tracking. Matches by
 * canonical side name because the two export entry points store the same side under different
 * raw spellings (e.g. "sleeve_left" vs "left_sleeve").
 */
export function clearTrackedSideExport(
  tracking: { sideJobIds: Map<string, string>; sideDownloadUrls?: Map<string, string> } | undefined,
  sideName: string,
  canonicalSide: (name: string) => string
): void {
  if (!tracking) {
    return;
  }
  const target = canonicalSide(sideName);
  [tracking.sideJobIds, tracking.sideDownloadUrls].forEach(map => {
    if (!map) {
      return;
    }
    [...map.keys()].forEach(k => {
      if (k === sideName || canonicalSide(k) === target) {
        map.delete(k);
      }
    });
  });
}
